import { useMemo, useState } from "react";
import { ClipboardList, FolderTree, Plus } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  SUPPORT_FORM_FIELD_TYPES,
  SUPPORT_FORM_FIELD_TYPE_LABELS,
  type SupportFormField,
  type SupportFormFieldType,
} from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "../stores/toast";
import { useBottomBarActions } from "../hooks/useBottomBarActions";
import {
  Button,
  Input,
  SearchSelect,
  Textarea,
  Badge,
  Toggle,
  useConfirm,
  PillSegmentSwitcher,
} from "../components/ui";
import { DataTable, type Column, type DataTableRowActions } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";

const WORKFLOW_OPTIONS = [
  { value: "refund", label: "Refund" },
  { value: "not_delivered", label: "Not delivered" },
  { value: "general", label: "General" },
  { value: "custom", label: "Custom" },
] as const;

const FIELD_TYPE_OPTIONS = SUPPORT_FORM_FIELD_TYPES.map((value) => ({
  value,
  label: SUPPORT_FORM_FIELD_TYPE_LABELS[value],
}));

type WorkflowKey = "refund" | "not_delivered" | "general" | "custom";
type HubTab = "forms" | "categories";

interface FormCategoryTreeNode {
  id: string;
  name: string;
  slug: string;
  sortOrder: number;
  formCount: number;
  subCategories: {
    id: string;
    name: string;
    slug: string;
    sortOrder: number;
    parentId: string;
    formCount: number;
  }[];
}

interface DynamicForm {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  workflowKey: WorkflowKey;
  active: boolean;
  subCategoryId: string | null;
  fields: SupportFormField[];
  createdAt: string;
  subCategory: {
    id: string;
    name: string;
    slug: string;
    category: { id: string; name: string; slug: string } | null;
  } | null;
}

interface FormState {
  name: string;
  slug: string;
  description: string;
  workflowKey: WorkflowKey;
  active: boolean;
  parentCategoryId: string;
  subCategoryId: string;
  fields: SupportFormField[];
}

interface CategoryEditorState {
  id?: string;
  name: string;
  slug: string;
  sortOrder: number;
  parentId: string | null;
}

const emptyField = (): SupportFormField => ({
  key: "",
  label: "",
  type: "text",
  required: false,
  options: [],
});

const emptyForm = (): FormState => ({
  name: "",
  slug: "",
  description: "",
  workflowKey: "general",
  active: true,
  parentCategoryId: "",
  subCategoryId: "",
  fields: [emptyField()],
});

export function AiForms() {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [hubTab, setHubTab] = useState<HubTab>("forms");

  const [editor, setEditor] = useState<DynamicForm | "new" | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());

  const [categoryEditor, setCategoryEditor] = useState<CategoryEditorState | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["ai-forms"],
    queryFn: () => api<{ items: DynamicForm[] }>("/ai/forms"),
  });

  const { data: categoryTree, isLoading: categoriesLoading } = useQuery({
    queryKey: ["ai-form-categories"],
    queryFn: () => api<{ items: FormCategoryTreeNode[] }>("/ai/form-categories"),
  });

  const saveMut = useMutation({
    mutationFn: (payload: FormState & { id?: string }) => {
      const body = {
        name: payload.name,
        slug: payload.slug,
        description: payload.description || null,
        workflowKey: payload.workflowKey,
        active: payload.active,
        subCategoryId: payload.subCategoryId || null,
        fields: payload.fields.map((f) => ({
          ...f,
          options: f.type === "select" ? (f.options ?? []).filter(Boolean) : undefined,
        })),
      };
      if (payload.id) return api(`/ai/forms/${payload.id}`, { method: "PUT", body });
      return api<{ id: string }>("/ai/forms", { method: "POST", body });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-forms"] });
      void qc.invalidateQueries({ queryKey: ["ai-form-categories"] });
      toast("success", editor === "new" ? "Form created" : "Form updated");
      closeEditor();
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Save failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api(`/ai/forms/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-forms"] });
      void qc.invalidateQueries({ queryKey: ["ai-form-categories"] });
      toast("success", "Form deleted");
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Delete failed"),
  });

  const saveCategoryMut = useMutation({
    mutationFn: (payload: CategoryEditorState) => {
      const body = {
        name: payload.name,
        slug: payload.slug || undefined,
        sortOrder: payload.sortOrder,
        parentId: payload.parentId,
      };
      if (payload.id) return api(`/ai/form-categories/${payload.id}`, { method: "PUT", body });
      return api<{ id: string }>("/ai/form-categories", { method: "POST", body });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-form-categories"] });
      toast("success", categoryEditor?.id ? "Category updated" : "Category created");
      setCategoryEditor(null);
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Save failed"),
  });

  const deleteCategoryMut = useMutation({
    mutationFn: (id: string) => api(`/ai/form-categories/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ai-form-categories"] });
      toast("success", "Category deleted");
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Delete failed"),
  });

  const parentCategoryOptions = useMemo(
    () => (categoryTree?.items ?? []).map((c) => ({ value: c.id, label: c.name })),
    [categoryTree],
  );

  const subCategoryOptions = useMemo(() => {
    if (!form.parentCategoryId) return [];
    const parent = categoryTree?.items.find((c) => c.id === form.parentCategoryId);
    return (parent?.subCategories ?? []).map((s) => ({ value: s.id, label: s.name }));
  }, [categoryTree, form.parentCategoryId]);

  function openCreate() {
    setEditor("new");
    setForm(emptyForm());
  }

  function openEdit(row: DynamicForm) {
    setEditor(row);
    setForm({
      name: row.name,
      slug: row.slug,
      description: row.description ?? "",
      workflowKey: row.workflowKey,
      active: row.active,
      parentCategoryId: row.subCategory?.category?.id ?? "",
      subCategoryId: row.subCategoryId ?? "",
      fields: row.fields.length ? row.fields.map((f) => ({ ...f, options: f.options ?? [] })) : [emptyField()],
    });
  }

  function closeEditor() {
    setEditor(null);
    setForm(emptyForm());
  }

  useBottomBarActions(() => {
    if (hubTab === "categories") {
      if (categoryEditor) {
        return [
          { id: "cancel", label: "Cancel", onClick: () => setCategoryEditor(null) },
          {
            id: "save",
            label: "Save",
            variant: "primary" as const,
            disabled: saveCategoryMut.isPending || categoryEditor.name.trim().length < 2,
            onClick: () => saveCategoryMut.mutate(categoryEditor),
          },
        ];
      }
      return [
        {
          id: "create-cat",
          label: "Create",
          icon: Plus,
          variant: "primary" as const,
          onClick: () =>
            setCategoryEditor({ name: "", slug: "", sortOrder: 0, parentId: null }),
        },
      ];
    }
    if (editor) {
      return [
        { id: "cancel", label: "Cancel", onClick: closeEditor },
        {
          id: "save",
          label: "Save",
          variant: "primary" as const,
          disabled:
            saveMut.isPending || !form.name || !form.slug || form.fields.every((f) => !f.key),
          onClick: () =>
            saveMut.mutate({
              ...form,
              id: editor !== "new" && editor ? editor.id : undefined,
            }),
        },
      ];
    }
    return [{ id: "create", label: "Create", icon: Plus, variant: "primary" as const, onClick: openCreate }];
  });

  function updateField(index: number, patch: Partial<SupportFormField>) {
    setForm((f) => ({
      ...f,
      fields: f.fields.map((field, i) => (i === index ? { ...field, ...patch } : field)),
    }));
  }

  function addField() {
    setForm((f) => ({ ...f, fields: [...f.fields, emptyField()] }));
  }

  function removeField(index: number) {
    setForm((f) => ({ ...f, fields: f.fields.filter((_, i) => i !== index) }));
  }

  const columns: Column<DynamicForm>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      sortValue: (r) => r.name,
      render: (r) => <span className="font-medium">{r.name}</span>,
    },
    {
      key: "category",
      header: "Category",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.subCategory?.category?.name ?? "",
      render: (r) =>
        r.subCategory?.category ? (
          <span className="text-xs text-white/60">
            {r.subCategory.category.name} · {r.subCategory.name}
          </span>
        ) : (
          <span className="text-xs text-white/35">—</span>
        ),
    },
    {
      key: "slug",
      header: "Slug",
      sortable: true,
      sortValue: (r) => r.slug,
      exportValue: (r) => `/${r.slug}`,
      render: (r) => <span className="font-mono text-xs text-white/60">/{r.slug}</span>,
    },
    {
      key: "workflow",
      header: "Workflow",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.workflowKey,
      render: (r) => <Badge color="blue">{r.workflowKey.replace(/_/g, " ")}</Badge>,
    },
    {
      key: "fields",
      header: "Fields",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.fields.length,
      render: (r) => <span className="tabular-nums">{r.fields.length}</span>,
    },
    {
      key: "active",
      header: "Active",
      sortable: true,
      sortValue: (r) => r.active,
      exportValue: (r) => (r.active ? "Yes" : "No"),
      render: (r) => (r.active ? <Badge color="green">Yes</Badge> : <Badge color="gray">No</Badge>),
    },
    {
      key: "created",
      header: "Created",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.createdAt,
      exportValue: (r) => fmtDate(r.createdAt),
      render: (r) => <span className="text-xs text-white/50">{fmtDate(r.createdAt)}</span>,
    },
  ];

  const rowActions = useMemo(
    (): DataTableRowActions<DynamicForm> => ({
      extra: (r) => [
        {
          label: "Delete",
          variant: "danger",
          onClick: () =>
            confirm({
              title: "Delete form?",
              message: "Existing cases will keep their data.",
              danger: true,
              confirmLabel: "Delete",
              action: () => deleteMut.mutateAsync(r.id),
            }),
        },
      ],
    }),
    [confirm, deleteMut],
  );

  const hubViews = [
    { value: "forms" as const, label: "Forms" },
    { value: "categories" as const, label: "Categories" },
  ];

  return (
    <ListPageShell>
      {dialog}

      <div className="shrink-0">
        <PillSegmentSwitcher options={hubViews} value={hubTab} onChange={setHubTab} />
      </div>

      {hubTab === "forms" ? (
        <DataTable
          columns={columns}
          rows={data?.items}
          rowKey={(r) => r.id}
          loading={isLoading}
          exportFilename="ai-forms"
          toolbar={{ onRefresh: () => void qc.invalidateQueries({ queryKey: ["ai-forms"] }) }}
          onRowClick={openEdit}
          rowActions={rowActions}
          empty={{
            icon: ClipboardList,
            title: "No dynamic forms",
            hint: "Create a form for customer support workflows.",
          }}
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto rounded-2xl border border-line bg-surface p-4">
          {categoriesLoading ? (
            <p className="text-xs text-muted">Loading…</p>
          ) : !categoryTree?.items.length ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <FolderTree className="text-muted" size={32} />
              <p className="text-sm text-foreground">No categories yet</p>
              <p className="text-xs text-muted">Create a category, then add sub-categories for grouping on Support.</p>
            </div>
          ) : (
            <ul className="space-y-4">
              {categoryTree.items.map((cat) => (
                <li key={cat.id} className="rounded-xl border border-line/60 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium">{cat.name}</p>
                      <p className="text-[10px] text-muted">
                        /{cat.slug} · sort {cat.sortOrder}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setCategoryEditor({
                            id: cat.id,
                            name: cat.name,
                            slug: cat.slug,
                            sortOrder: cat.sortOrder,
                            parentId: null,
                          })
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setCategoryEditor({
                            name: "",
                            slug: "",
                            sortOrder: cat.subCategories.length,
                            parentId: cat.id,
                          })
                        }
                      >
                        Add sub-category
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          confirm({
                            title: "Delete category?",
                            message: "Remove sub-categories and reassign forms first.",
                            danger: true,
                            confirmLabel: "Delete",
                            action: () => deleteCategoryMut.mutateAsync(cat.id),
                          })
                        }
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  {cat.subCategories.length > 0 && (
                    <ul className="mt-3 space-y-2 border-l border-line pl-3">
                      {cat.subCategories.map((sub) => (
                        <li key={sub.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                          <div>
                            <span>{sub.name}</span>
                            <span className="ml-2 text-[10px] text-muted">
                              {sub.formCount} form{sub.formCount === 1 ? "" : "s"}
                            </span>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setCategoryEditor({
                                  id: sub.id,
                                  name: sub.name,
                                  slug: sub.slug,
                                  sortOrder: sub.sortOrder,
                                  parentId: sub.parentId,
                                })
                              }
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                confirm({
                                  title: "Delete sub-category?",
                                  message: "Reassign or delete linked forms first.",
                                  danger: true,
                                  confirmLabel: "Delete",
                                  action: () => deleteCategoryMut.mutateAsync(sub.id),
                                })
                              }
                            >
                              Delete
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <RightSidebarPanel
        open={editor !== null}
        wide
        title={editor === "new" ? "New form" : "Edit form"}
        subtitle={form.slug ? `/${form.slug}` : undefined}
        onClose={closeEditor}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={closeEditor}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={saveMut.isPending || !form.name || !form.slug || form.fields.every((f) => !f.key)}
              onClick={() =>
                saveMut.mutate({
                  ...form,
                  id: editor !== "new" && editor ? editor.id : undefined,
                })
              }
            >
              Save
            </Button>
          </div>
        }
      >
        <div className="sidebar-form">
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input
            label="Slug"
            value={form.slug}
            onChange={(e) =>
              setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })
            }
            hint="Lowercase letters, numbers, hyphens — used in /ai/forms/:slug/submit"
          />
          <Textarea
            label="Description"
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <SearchSelect
            label="Category"
            options={[{ value: "", label: "None" }, ...parentCategoryOptions]}
            value={form.parentCategoryId}
            onChange={(v) => setForm({ ...form, parentCategoryId: v, subCategoryId: "" })}
          />
          <SearchSelect
            label="Sub-category"
            options={[{ value: "", label: "None" }, ...subCategoryOptions]}
            value={form.subCategoryId}
            onChange={(v) => setForm({ ...form, subCategoryId: v })}
            hint={form.parentCategoryId ? undefined : "Select a category first"}
          />
          <SearchSelect
            label="Workflow"
            options={[...WORKFLOW_OPTIONS]}
            value={form.workflowKey}
            onChange={(v) => setForm({ ...form, workflowKey: v as WorkflowKey })}
          />
          <Toggle label="Active" checked={form.active} onChange={(v) => setForm({ ...form, active: v })} />

          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/40">Fields</p>
            <Button variant="ghost" size="sm" onClick={addField}>
              Add field
            </Button>
          </div>

          {form.fields.map((field, fi) => (
            <div key={fi} className="space-y-2 rounded-xl border border-line bg-white/[0.02] p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs font-medium text-white/60">Field {fi + 1}</p>
                {form.fields.length > 1 && (
                  <Button variant="ghost" size="sm" onClick={() => removeField(fi)}>
                    Remove
                  </Button>
                )}
              </div>
              <div className="form-row-2">
                <Input
                  label="Key"
                  value={field.key}
                  onChange={(e) => updateField(fi, { key: e.target.value.replace(/\s/g, "_") })}
                />
                <Input label="Label" value={field.label} onChange={(e) => updateField(fi, { label: e.target.value })} />
              </div>
              <SearchSelect
                label="Type"
                options={[...FIELD_TYPE_OPTIONS]}
                value={field.type}
                onChange={(v) => updateField(fi, { type: v as SupportFormFieldType })}
              />
              {field.type === "select" && (
                <Input
                  label="Options"
                  hint="Comma-separated"
                  value={(field.options ?? []).join(", ")}
                  onChange={(e) =>
                    updateField(fi, {
                      options: e.target.value
                        .split(",")
                        .map((o) => o.trim())
                        .filter(Boolean),
                    })
                  }
                />
              )}
              <Toggle
                label="Required"
                checked={field.required}
                onChange={(v) => updateField(fi, { required: v })}
              />
            </div>
          ))}
        </div>
      </RightSidebarPanel>

      <RightSidebarPanel
        open={categoryEditor !== null}
        title={categoryEditor?.id ? "Edit category" : categoryEditor?.parentId ? "New sub-category" : "New category"}
        onClose={() => setCategoryEditor(null)}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setCategoryEditor(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={saveCategoryMut.isPending || !categoryEditor?.name.trim()}
              onClick={() => categoryEditor && saveCategoryMut.mutate(categoryEditor)}
            >
              Save
            </Button>
          </div>
        }
      >
        {categoryEditor && (
          <div className="sidebar-form">
            <Input
              label="Name"
              value={categoryEditor.name}
              onChange={(e) =>
                setCategoryEditor({
                  ...categoryEditor,
                  name: e.target.value,
                  slug: categoryEditor.id
                    ? categoryEditor.slug
                    : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
                })
              }
            />
            <Input
              label="Slug"
              value={categoryEditor.slug}
              onChange={(e) =>
                setCategoryEditor({
                  ...categoryEditor,
                  slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
                })
              }
            />
            <Input
              label="Sort order"
              type="number"
              value={String(categoryEditor.sortOrder)}
              onChange={(e) =>
                setCategoryEditor({ ...categoryEditor, sortOrder: Number(e.target.value) || 0 })
              }
            />
          </div>
        )}
      </RightSidebarPanel>
    </ListPageShell>
  );
}
